import { filter, map, Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';

import { DoodModalModel } from '@core/models/modal.model';

import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { ModalStoreDispatcher } from '@common/dispatchers/modal.dispatcher';

@Injectable({
  providedIn: 'root',
})
/**
 * Usage example:
 *
 * 1. Define the modal's inputs and outputs:
 * ```typescript
 * export type ShopSearchParametersModalInputs = {
 *   overrideSelectedDistributionMode?: string;
 * };
 *
 * export type ShopSearchParametersModalEvents = {
 *   type: 'updated';
 *   success: boolean;
 * };
 * ```
 *
 * 2. Emit an event:
 * ```typescript
 * this.modalsService.emitEvent({ type: 'updated', success: true });
 * ```
 *
 * 3. Retrieve the input data:
 * ```typescript
 * data$ = this.modalsService.getData(ShopSearchParametersModalComponent.handle);
 * ```
 *
 * 4. From the component that opens the modal, inject `ModalsService`:
 * ```typescript
 * private readonly modalsService: ModalsService<
 *   ShopSearchParametersModalInputs,
 *   ShopSearchParametersModalEvents
 * >;
 * ```
 *
 * 5. Listen to the events:
 * ```typescript
 * event$ = this.modalsService.event$(handle);
 * ```
 */
export class ModalsService<
  TInputs extends Record<string, unknown> = { [key: string]: unknown },
  TEvents = unknown,
> {
  private subjectSource = new Subject<{ handle: string; event: TEvents }>();
  /**
   * Listen to the open modal events.
   * Limitation if the same modal (same handle) is opened multiple times, the event will be emitted
   * for all of them.
   */
  event$ = (handle: string) =>
    this.subjectSource.pipe(
      filter(({ handle: h }) => h === handle),
      map(({ event }) => event),
    );

  constructor(
    private modalSelector: ModalStoreSelector,
    private modalDispatcher: ModalStoreDispatcher,
  ) {}

  open(handle: string, attribute?: string, data?: TInputs): void {
    const allModals = this.modalSelector.modals;
    const lastModal = allModals[0];
    this.modalDispatcher.addModal({
      handle,
      attribute,
      data,
      index: ((lastModal?.index ? lastModal.index / 10 : 0) + 1) * 10,
    });
  }

  close(handle: string): void {
    this.modalDispatcher.removeModal(handle);
  }

  isModalLink(link: string): boolean {
    return link.startsWith('modal://');
  }

  openFromLink(link: string): void {
    const urlWithHttp = link.replace('modal://', 'http://');
    const url = new URL(urlWithHttp);
    const params = new URLSearchParams(url.search);
    const inputs: { [key: string]: unknown } = {};
    params.forEach((value, key) => {
      inputs[key] = value;
    });
    const handle = url.host;
    this.open(handle, undefined, inputs as TInputs);
  }

  setData(handle: string, data: TInputs): void {
    this.modalDispatcher.updateModal({
      id: handle,
      changes: { data },
    });
  }

  getData(handle: string): Observable<DoodModalModel<TInputs> | undefined> {
    return this.modalSelector.selectModal(handle);
  }

  emitEvent({ data, handle }: { data: TEvents; handle: string }): void {
    this.subjectSource.next({ handle, event: data });
  }
}
